import { useState, useMemo, useEffect, useCallback } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import { DockCard } from '../components/docks/DockCard';
import { DockBoardToolbar } from '../components/docks/DockBoardToolbar';
import { DockScheduleDrawer } from '../components/docks/DockScheduleDrawer';
import { BlackoutList, type BlackoutWindow } from '../components/docks/BlackoutList';
import { useLocation } from 'wouter';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getDockDisplayStatus } from '../lib/dock-utils';
import { dockBoardKey } from '../lib/swrKeys';
import { dockMutations } from '../lib/dockMutations';
import { ShieldOff, ChevronDown, ChevronUp } from 'lucide-react';
import './DockManagement.css';

// Match Backend Response & DockCard expectation
interface AppointmentSnippet {
    id: string;
    status: string;
    carrierName?: string;
    customerPoNumber?: string;
    startAt: string;
    checkInTime?: string;
}

interface Dock {
    id: string;
    dockNumber: string;
    type?: string;
    status: 'Available' | 'Occupied' | 'Maintenance' | 'Out of Service' | string;
    now?: AppointmentSnippet | null;
    next?: AppointmentSnippet | null;
    updatedAt?: string;
    capacity?: number;
    effectiveStatus?: 'Available' | 'Occupied' | 'Maintenance' | 'Out of Service' | string;
    // INV-3: Dock capability tags
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
    // Exception fields (for DockScheduleDrawer compat)
    exceptionOwnerUserId?: string | null;
    exceptionReason?: string | null;
    exceptionNote?: string | null;
    exceptionResolvedAt?: string | null;
    exceptionUpdatedAt?: string | null;
}

export function DockManagement() {
    const { user } = useAuthStore();
    const [location, setLocation] = useLocation();

    // Parse URL search params
    // Parse URL search params with Validation
    const getSearchParams = () => new URLSearchParams(window.location.search);

    // Define Allowed Statuses (System + All)
    const ALLOWED_STATUS = new Set(['All', 'Available', 'Occupied', 'Maintenance', 'Out of Service']);

    const [statusFilter, setStatusFilter] = useState(() => {
        const params = getSearchParams();
        const rawStatus = params.get('status');
        if (rawStatus && !ALLOWED_STATUS.has(rawStatus)) {
            return 'All'; // Fallback for invalid status
        }
        return rawStatus || 'All';
    });

    // Check for invalid status on mount to notify user 
    // (We do this separate from the sync effect to avoid race conditions with URL cleaning)
    useEffect(() => {
        const raw = new URLSearchParams(window.location.search).get('status');
        if (raw && !ALLOWED_STATUS.has(raw)) {
            toast.error(`Invalid filter "${raw}" reset to All`);
            // Force URL cleanup immediately in case the sync effect misses (it shouldn't, but safe)
            const params = new URLSearchParams(window.location.search);
            params.delete('status');
            const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
            window.history.replaceState(null, '', newUrl);
        }
    }, []);

    const [searchQuery, setSearchQuery] = useState('');
    // const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'All'); // Replaced above
    const [typeFilter, setTypeFilter] = useState('All');

    // Filter & Sort State
    const [sortBy, setSortBy] = useState('number');
    const [showExceptions, setShowExceptions] = useState(false);

    // Sync statusFilter to URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (statusFilter === 'All') {
            params.delete('status');
        } else {
            params.set('status', statusFilter);
        }

        const newSearch = params.toString();
        const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`;
        window.history.replaceState(null, '', newUrl);

    }, [statusFilter]);

    // Modal States
    const [showGenericModal, setShowGenericModal] = useState(false); // For Add/Edit
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [editingDock, setEditingDock] = useState<Dock | null>(null);

    // Drawer State
    const [selectedDockId, setSelectedDockId] = useState<string | null>(null);

    // V1.3B: Blackout state
    const [showBlackoutList, setShowBlackoutList] = useState(true);

    // Navigation State Cleanup
    // Navigation State Cleanup
    // const [location] = useLocation(); // Moved up

    useEffect(() => {
        // Safer cleanup: Clear all ephemeral state on any route change
        setDeleteId(null);
        setShowGenericModal(false);
        setSelectedDockId(null);
    }, [location]);

    // Initial Form State
    const initialFormState = {
        dockNumber: '',
        capacity: 0,
        temperatureZone: '',
        handlingCapabilities: '' as string,
    };
    const [formData, setFormData] = useState(initialFormState);

    // SWR Data Fetching
    const { data, isLoading, error } = useSWR<{ docks: Dock[] }>(
        user?.warehouseId ? dockBoardKey() : null,
        () => api.getDockBoard(),
        {
            refreshInterval: 15000,
            revalidateOnFocus: true,
            onError: (err) => {
                // If 500 and message contains "migration" or "column", we might need upgrade
                console.error("Dock Board Fetch Error:", err);
            }
        }
    );

    // V1.3B: Blackout SWR
    const { token } = useAuthStore();
    const blackoutFetcher = useCallback(async () => {
        const res = await fetch('/api/docks/blackouts', {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch blackouts');
        return res.json();
    }, [token]);

    const { data: blackoutData, isLoading: blackoutsLoading, mutate: mutateBlackouts } = useSWR<{ blackouts: BlackoutWindow[] }>(
        user?.warehouseId && token ? '/api/docks/blackouts' : null,
        blackoutFetcher,
        { refreshInterval: 30000, revalidateOnFocus: true }
    );

    // GUARDRAIL: Database Upgrade Required
    // If the error suggests missing schema, block usage to prevent corruption/confusion.
    const isDbError = error?.status === 500 || (error?.message && error.message.toLowerCase().includes('column'));
    if (isDbError) {
        return (
            <div className="p-10 flex flex-col items-center justify-center min-h-screen bg-background text-foreground text-center animate-in fade-in">
                <div className="bg-destructive/10 p-8 rounded-2xl border-2 border-destructive/20 max-w-lg">
                    <h1 className="text-3xl font-bold text-destructive mb-4">Database Upgrade Required</h1>
                    <p className="text-lg text-muted-foreground mb-6">
                        The application has been updated, but the database schema is syncing.
                    </p>
                    <div className="bg-card p-4 rounded-md border text-left font-mono text-sm mb-6 text-red-500 overflow-auto max-h-40">
                        {error?.message || "Missing required columns"}
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl"
                    >
                        Check Again
                    </button>
                    <p className="mt-4 text-xs text-muted-foreground">
                        If this persists, ask an admin to run: <code className="bg-muted px-1 rounded">POST /api/debug/migrate-latest</code>
                    </p>
                </div>
            </div>
        );
    }

    const docks = data?.docks || [];

    // Filter Logic
    const filteredDocks = useMemo(() => {
        let result = docks.filter(dock => {
            const q = searchQuery.toLowerCase();
            const matchesSearch =
                (dock.dockNumber ?? '').toLowerCase().includes(q) ||
                (dock.now?.carrierName ?? '').toLowerCase().includes(q) ||
                (dock.now?.customerPoNumber ?? '').toLowerCase().includes(q) ||
                (dock.next?.carrierName ?? '').toLowerCase().includes(q) ||
                (dock.next?.customerPoNumber ?? '').toLowerCase().includes(q);

            const statusForUi = getDockDisplayStatus(dock);
            const matchesStatus = statusFilter === 'All' || statusForUi === statusFilter;
            const matchesType = typeFilter === 'All' || dock.type === typeFilter;

            return matchesSearch && matchesStatus && matchesType;
        });

        // Exception Filter
        if (showExceptions) {
            result = result.filter(dock => {
                const status = getDockDisplayStatus(dock);
                let isException = false;

                // Over SLA or Critical Dwell
                if (status === 'Occupied' && dock.now?.checkInTime) {
                    const diffMinutes = (new Date().getTime() - new Date(dock.now.checkInTime).getTime()) / 60000;
                    if (diffMinutes > 60) isException = true;
                }

                // Late Upcoming
                if (dock.next?.startAt) {
                    const start = new Date(dock.next.startAt);
                    if (new Date() > new Date(start.getTime() + 15 * 60000)) {
                        isException = true;
                    }
                }

                return isException;
            });
        }

        // Sort Logic
        result.sort((a, b) => {
            if (sortBy === 'number') {
                return a.dockNumber.localeCompare(b.dockNumber, undefined, { numeric: true });
            }
            if (sortBy === 'status') {
                // Priority: Occupied > Available > Maintenance > OOS
                const priority = { 'Occupied': 0, 'Available': 1, 'Maintenance': 2, 'Out of Service': 3 };
                const sA = getDockDisplayStatus(a);
                const sB = getDockDisplayStatus(b);
                // @ts-ignore
                return (priority[sA] ?? 4) - (priority[sB] ?? 4);
            }
            if (sortBy === 'dwell') {
                // Worst Dwell First (Oldest checkInTime first)
                // If no checkInTime, put at bottom
                const tA = a.now?.checkInTime ? new Date(a.now.checkInTime).getTime() : Number.MAX_VALUE;
                const tB = b.now?.checkInTime ? new Date(b.now.checkInTime).getTime() : Number.MAX_VALUE;
                return tA - tB;
            }
            if (sortBy === 'updated') {
                const tA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                const tB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                return tB - tA; // Newest first
            }
            return 0;
        });

        return result;
    }, [docks, searchQuery, statusFilter, typeFilter, showExceptions, sortBy]);

    // Handlers
    const handleStatusChange = async (dockId: string, newStatus: string) => {
        await dockMutations.updateDockStatus(dockId, newStatus, docks);
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await dockMutations.deleteDock(deleteId);
            setDeleteId(null);
        } catch (error: any) {
            alert(error.message || 'Failed to delete dock');
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        const cleanNumber = formData.dockNumber.trim();
        if (!cleanNumber) {
            toast.error('Dock number is required');
            return;
        }
        const duplicate = docks.some(
            d => d.id !== editingDock?.id && d.dockNumber.trim().toLowerCase() === cleanNumber.toLowerCase()
        );
        if (duplicate) {
            toast.error(`Dock "${cleanNumber}" already exists in this warehouse.`);
            return;
        }
        try {
            if (editingDock) {
                await dockMutations.editDock(editingDock.id, { ...formData, dockNumber: cleanNumber });
            } else {
                if (!user?.warehouseId) return;
                await dockMutations.createDock(user.warehouseId, { ...formData, dockNumber: cleanNumber });
            }
            setShowGenericModal(false);
            setEditingDock(null);
            setFormData(initialFormState);

            // Return to checklist context if launched from onboarding
            if (!editingDock && new URLSearchParams(window.location.search).get('from') === 'onboarding') {
                setLocation('/dashboard?from=onboarding');
            }
        } catch (error: any) {
            toast.error(error.message || 'Failed to save dock');
        }
    };

    const openCreateModal = () => {
        setEditingDock(null);
        setFormData(initialFormState);
        setShowGenericModal(true);
        setDeleteId(null);
    };

    const openEditModal = (dock: Dock) => {
        setEditingDock(dock);
        setFormData({
            dockNumber: dock.dockNumber,
            capacity: dock.capacity || 0,
            temperatureZone: dock.temperatureZone || '',
            handlingCapabilities: dock.handlingCapabilities || '',
        });
        setShowGenericModal(true);
        setDeleteId(null); // Ensure no overlap
    };

    // Render Helpers
    if (!user?.warehouseId) return <div className="p-8">No warehouse assigned.</div>;

    const selectedDock = docks.find(d => d.id === selectedDockId);

    return (
        <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-background text-foreground">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pl-12 md:pl-0">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Dock Operations</h1>
                    <p className="text-muted-foreground mt-1">Real-time status and assignment board</p>
                </div>
                {(user.role === 'warehouse_manager' || user.role === 'admin') && (
                    <div className="flex items-center gap-2">
                        <button
                            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium transition-colors"
                            onClick={openCreateModal}
                        >
                            + Add Dock
                        </button>
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <DockBoardToolbar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                sortBy={sortBy}
                onSortChange={setSortBy}
                showExceptions={showExceptions}
                onShowExceptionsChange={setShowExceptions}
            />

            {/* Grid Content */}
            {isLoading ? (
                <DockGridSkeleton />
            ) : docks.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed rounded-xl bg-card space-y-3">
                    <h3 className="text-lg font-semibold text-foreground">No docks configured yet</h3>
                    <p className="text-muted-foreground text-sm max-w-md mx-auto">
                        Add your loading docks (e.g., D1, D2) to begin assigning appointments and tracking live dock status.
                    </p>
                    {(user.role === 'warehouse_manager' || user.role === 'admin') && (
                        <button
                            type="button"
                            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium transition-colors mt-2"
                            onClick={openCreateModal}
                        >
                            + Add Your First Dock
                        </button>
                    )}
                </div>
            ) : filteredDocks.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed rounded-xl bg-card">
                    <p className="text-muted-foreground">No docks found matching your filters.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredDocks.map(dock => (
                        <DockCard
                            key={dock.id}
                            dock={dock}
                            onStatusChange={handleStatusChange}
                            onEdit={openEditModal}
                            onDelete={(id) => setDeleteId(id)}
                            onViewSchedule={(id) => setSelectedDockId(id)}
                        />
                    ))}
                </div>
            )}

            {/* V1.3B: Blackout Windows Section */}
            {(user.role === 'warehouse_manager' || user.role === 'warehouse_staff' || user.role === 'admin') && (
                <div className="mt-8">
                    <button
                        onClick={() => setShowBlackoutList(!showBlackoutList)}
                        className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white transition-colors"
                    >
                        <ShieldOff className="w-4 h-4 text-red-500" />
                        Blackout Windows
                        <span className="text-xs font-normal text-slate-400 dark:text-slate-500">
                            ({(blackoutData?.blackouts || []).filter(b => new Date(b.endAt).getTime() > Date.now()).length})
                        </span>
                        {showBlackoutList ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>
                    {showBlackoutList && (
                        <BlackoutList
                            blackouts={blackoutData?.blackouts || []}
                            loading={blackoutsLoading}
                            onDeleted={() => mutateBlackouts()}
                        />
                    )}
                </div>
            )}

            {/* Dock Detail Modal (centered) */}
            <DockScheduleDrawer
                dockId={selectedDockId}
                isOpen={!!selectedDockId}
                onClose={() => setSelectedDockId(null)}
                dockNumber={selectedDock?.dockNumber}
                dock={selectedDock}
                onStatusChange={(id, status) => { setSelectedDockId(null); handleStatusChange(id, status); }}
                onEdit={(dock) => { setSelectedDockId(null); openEditModal(dock as Dock); }}
            />

            {/* Create/Edit Modal */}
            {showGenericModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-card w-full max-w-md rounded-xl shadow-xl border p-6 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <h2 className="text-xl font-bold mb-4">{editingDock ? 'Edit Dock' : 'Add New Dock'}</h2>
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Dock Number</label>
                                <input
                                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
                                    value={formData.dockNumber}
                                    onChange={e => setFormData({ ...formData, dockNumber: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Capacity (Pallets)</label>
                                <input
                                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
                                    type="number"
                                    value={formData.capacity}
                                    onChange={e => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                                />
                            </div>
                            {/* INV-3: Temperature Zone */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Temperature Zone</label>
                                <select
                                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none"
                                    value={formData.temperatureZone}
                                    onChange={e => setFormData({ ...formData, temperatureZone: e.target.value })}
                                >
                                    <option value="">Not set</option>
                                    <option value="ambient">Ambient</option>
                                    <option value="chilled">Chilled</option>
                                    <option value="frozen">Frozen</option>
                                    <option value="heated">Heated</option>
                                    <option value="mixed">Mixed-use</option>
                                </select>
                                <p className="text-xs text-muted-foreground">Optional — helps match POs to compatible docks</p>
                            </div>
                            {/* INV-3: Handling Capabilities */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Handling Capabilities</label>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {[
                                        { value: 'forklift', label: 'Forklift' },
                                        { value: 'pallet_jack', label: 'Pallet jack' },
                                        { value: 'clamp', label: 'Clamp' },
                                        { value: 'slip_sheet', label: 'Slip-sheet' },
                                        { value: 'floor_load', label: 'Floor-load' },
                                        { value: 'hand_unload', label: 'Hand unload' },
                                    ].map(opt => {
                                        const caps = formData.handlingCapabilities.split(',').map(c => c.trim()).filter(Boolean);
                                        const checked = caps.includes(opt.value);
                                        return (
                                            <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1.5 py-1 transition-colors">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => {
                                                        const next = checked
                                                            ? caps.filter(c => c !== opt.value)
                                                            : [...caps, opt.value];
                                                        setFormData({ ...formData, handlingCapabilities: next.join(',') });
                                                    }}
                                                    className="rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                {opt.label}
                                            </label>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-muted-foreground">Optional — select all equipment this dock supports</p>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium hover:bg-secondary rounded-md transition-colors"
                                    onClick={() => setShowGenericModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
                                >
                                    {editingDock ? 'Save Changes' : 'Create Dock'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete this dock? This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700 text-white"
                            onClick={handleDelete}
                        >
                            Delete Dock
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function DockGridSkeleton() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-pulse">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="h-48 bg-muted/40 rounded-xl border"></div>
            ))}
        </div>
    );
}
